import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchInvoiceCollectCalculation,
  type CollectCalculation,
} from '@/integrations/faturamento/client';
import { useGatewayMaterials } from '@/hooks/useGatewayMaterials';
import { useGatewayPackagings } from '@/hooks/useGatewayPackagings';
import { withCollectLabels } from '@/utils/collectCalculationLabel';

interface UseInvoiceCollectCalculationParams {
  cycleId: string;
  collectId: string;
  /** Só busca quando o popover está aberto (on demand). */
  enabled: boolean;
}

const EMPTY_NAMES: ReadonlyMap<number, string> = new Map();

/**
 * Racional de cálculo de uma coleta (GR-497). Busca sob demanda — só dispara
 * quando `enabled` (popover aberto). O racional é imutável para um ciclo
 * fechado, então cacheia por alguns minutos.
 *
 * O rótulo de cada regra ("Óleo Alimentar · Bombona") é composto aqui com o catálogo do
 * musa-agreements: o faturamento-api só manda `material_id`/`packaging_id` e o nome da regra.
 */
export const useInvoiceCollectCalculation = ({
  cycleId,
  collectId,
  enabled,
}: UseInvoiceCollectCalculationParams) => {
  const { data: materials } = useGatewayMaterials({ enabled });
  const { data: packagings } = useGatewayPackagings({ enabled });

  const materialNames = useMemo(
    () => (materials ? new Map(materials.map((m) => [m.id, m.name])) : EMPTY_NAMES),
    [materials],
  );
  const packagingNames = useMemo(
    () => (packagings ? new Map(packagings.map((p) => [p.id, p.name])) : EMPTY_NAMES),
    [packagings],
  );

  const select = useCallback(
    (calculation: CollectCalculation) => withCollectLabels(calculation, materialNames, packagingNames),
    [materialNames, packagingNames],
  );

  return useQuery<CollectCalculation>({
    queryKey: ['invoice-collect-calculation', cycleId, collectId],
    queryFn: () => fetchInvoiceCollectCalculation(cycleId, collectId),
    select,
    enabled: enabled && !!cycleId && !!collectId,
    staleTime: 1000 * 60 * 5,
  });
};
